import { type Camera, followPlayer, screenToWorld } from "./client/camera";
import { attachInput, endInputFrame, moving, skillPressed, ultPressed } from "./client/input";
import { pickHitRegions, render, resultHitRegions, type Phase, type PickState } from "./client/render";
import { mountTuner, toggleTuner, tunerOpen } from "./client/tuner";
import { binds, loadTuning } from "./content/tuning";
import { createMatch, isPresent, tick, type PlayerInput, type Unit, type World } from "./sim";
import { dist } from "./sim/vec";

loadTuning();
mountTuner();

const canvas = document.getElementById("game") as HTMLCanvasElement;
const ctx = canvas.getContext("2d");
if (!ctx) throw new Error("Canvas 2D unavailable");

const input = attachInput(canvas);
const cam: Camera = { x: 14, y: 9 };
let phase: Phase = "pick";
const pick: PickState = { classId: "warrior" };
let world: World | null = null;
let last = performance.now();
let showBoard = false;

function resize(): void {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const w = window.innerWidth;
  const h = window.innerHeight;
  canvas.width = Math.floor(w * dpr);
  canvas.height = Math.floor(h * dpr);
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
}

window.addEventListener("resize", resize);
resize();

function hit(x: number, y: number, r: { x: number; y: number; w: number; h: number }): boolean {
  return x >= r.x && y >= r.y && x <= r.x + r.w && y <= r.y + r.h;
}

function startMatch(): void {
  world = createMatch({
    classId: pick.classId,
    seed: (Date.now() ^ 0x9e3779b9) >>> 0,
  });
  const p = world.units.find((u) => u.isPlayer);
  if (p) {
    cam.x = p.pos.x;
    cam.y = p.pos.y;
  }
  phase = "match";
}

function unitAt(w: World, x: number, y: number): Unit | null {
  let best: Unit | null = null;
  let bestD = 0.65;
  for (const u of w.units) {
    if (!isPresent(u)) continue;
    const d = dist(u.pos, { x, y });
    if (d < bestD) {
      best = u;
      bestD = d;
    }
  }
  return best;
}

function handleClicks(viewW: number, viewH: number): { clickedId: number | null; clearTarget: boolean } {
  const none = { clickedId: null as number | null, clearTarget: false };
  if (!input.mouseClicked) return none;
  const mx = input.mouseX;
  const my = input.mouseY;
  if (phase === "pick") {
    for (const r of pickHitRegions(viewW, viewH)) {
      if (!hit(mx, my, r)) continue;
      if (r.kind === "class" && r.id) pick.classId = r.id;
      else if (r.kind === "start") startMatch();
    }
    return none;
  }
  if (phase === "result") {
    for (const r of resultHitRegions(viewW, viewH)) {
      if (hit(mx, my, r) && r.kind === "again") {
        phase = "pick";
        world = null;
      }
    }
    return none;
  }
  if (phase === "match" && world) {
    if (my > viewH - 140) return none;
    const aim = screenToWorld(mx, my, cam, viewW, viewH);
    const u = unitAt(world, aim.x, aim.y);
    if (u && u.id !== world.playerId) return { clickedId: u.id, clearTarget: false };
    return { clickedId: null, clearTarget: true };
  }
  return none;
}

function playerInput(w: World, viewW: number, viewH: number, click: { clickedId: number | null; clearTarget: boolean }): PlayerInput {
  const unit = w.units.find((u) => u.id === w.playerId);
  const mv = moving(input);
  const aim = screenToWorld(input.mouseX, input.mouseY, cam, viewW, viewH);
  return {
    unitId: unit?.id ?? -1,
    moveX: mv.x,
    moveY: mv.y,
    aimX: aim.x,
    aimY: aim.y,
    skill: skillPressed(input),
    ult: ultPressed(input),
    clickedId: click.clickedId,
    clearTarget: click.clearTarget,
  };
}

function frame(now: number): void {
  const dt = Math.min(0.05, (now - last) / 1000);
  last = now;
  const viewW = canvas.clientWidth;
  const viewH = canvas.clientHeight;

  if (input.pressed.has(binds.tuner)) toggleTuner();
  if (input.pressed.has(binds.board)) showBoard = !showBoard;
  if (input.pressed.has("enter") && phase === "pick") startMatch();
  if (input.pressed.has("escape") && phase === "result") {
    phase = "pick";
    world = null;
  }

  const click = tunerOpen() ? { clickedId: null, clearTarget: false } : handleClicks(viewW, viewH);

  if (phase === "match" && world) {
    followPlayer(cam, world, dt);
    tick(world, dt, [playerInput(world, viewW, viewH, click)]);
    if (world.over) phase = "result";
  } else if (phase === "result" && world) {
    followPlayer(cam, world, dt);
  }

  render(ctx!, {
    phase,
    pick,
    world,
    cam,
    viewW,
    viewH,
    hover: { x: input.mouseX, y: input.mouseY },
    showBoard,
  });

  endInputFrame(input);
  requestAnimationFrame(frame);
}

requestAnimationFrame(frame);
